/**
 * Backend calls for the super-admin collateral-withdrawal treasury surface. Two endpoints on the
 * cards domain, both gated on the `superAdmin` role by the backend:
 *   - prepare: enforce allowlist + cap, fetch Rain's executor signature, persist a `requested`
 *     audit record, return the unsigned `withdrawAsset` for the connected wallet to sign + send.
 *   - report: after the console broadcasts + awaits the Base receipt, record `{ txHash, status }`.
 *
 * The backend never holds the admin key or touches the chain; the console does the signing (see
 * `@/wallet/collateralWithdraw`).
 */
import { adminRequest } from "./client";

/** The `withdrawAsset(proxy, asset, amount, recipient, expiresAt, salt, signature)` call materials. */
export interface UnsignedWithdrawAsset {
  chainId: number;
  /** The contract to call `withdrawAsset` on (the V1 collateral controller). */
  target: string;
  collateralProxy: string;
  token: string;
  /** Native on-chain amount (uint256 as a decimal string) the executor signed over. */
  amount: string;
  recipient: string;
  /** Rain signature expiry, in unix seconds. */
  expiresAt: number;
  /** Executor salt, as a 32-byte hex string. */
  salt: string;
  /** Rain executor signature, as a hex string. */
  signature: string;
}

/** The persisted `CollateralWithdrawal` record returned by prepare (subset the console needs). */
export interface PreparedWithdrawalRecord {
  id: string;
  amountMicroUsdc: number;
  recipient: string;
  adminAddress: string;
  state: string;
  createdAt: string;
  /** Passed back to `report` as the optimistic-concurrency check. */
  updatedAt: string;
}

export interface CollateralWithdrawalPreparedResponse {
  withdrawal: PreparedWithdrawalRecord;
  unsignedWithdrawAsset: UnsignedWithdrawAsset;
}

/** The current tenant collateral balance snapshot (`GET /cards/collateralWithdrawals/balance`). */
export interface CollateralBalance {
  chainId: number;
  /** The collateral contract (proxy) holding the funds. */
  proxy: string;
  /** The ERC-20 whose balance is reported (native USDC on Base). */
  token: string;
  /** The available collateral balance, as a decimal USDC string in human units (e.g. "3331.95"). */
  balance: string;
  /** The registered on-chain collateral-admin addresses. */
  adminAddresses: string[];
}

/** `GET /cards/collateralWithdrawals/balance` — read the tenant collateral balance (super-admin only). */
export function getCollateralBalance(token: string): Promise<CollateralBalance> {
  return adminRequest<CollateralBalance>({
    method: "GET",
    path: "/cards/collateralWithdrawals/balance",
    token,
  });
}

export type CollateralWithdrawalOutcome = "confirmed" | "reverted";

/** `POST /cards/collateralWithdrawals` — prepare a withdrawal (super-admin only). */
export function prepareCollateralWithdrawal(
  token: string,
  params: {
    amount: string;
    recipient: string;
    adminAddress: string;
    idempotencyKey: string;
  },
): Promise<CollateralWithdrawalPreparedResponse> {
  return adminRequest<CollateralWithdrawalPreparedResponse>({
    method: "POST",
    path: "/cards/collateralWithdrawals",
    body: params,
    token,
  });
}

/** `POST /cards/collateralWithdrawals/{id}/report` — record the on-chain outcome (super-admin only). */
export function reportCollateralWithdrawal(
  token: string,
  params: {
    id: string;
    updatedAt: string;
    txHash: string;
    status: CollateralWithdrawalOutcome;
  },
): Promise<unknown> {
  return adminRequest({
    method: "POST",
    path: `/cards/collateralWithdrawals/${params.id}/report`,
    query: { updatedAt: params.updatedAt },
    body: { txHash: params.txHash, status: params.status },
    token,
  });
}
